import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSharedLibrary, unshareVideo } from './shared-library';
import { editSharedVideo } from './api';
import { formatRelativeTime } from './ui';
import { config } from './config';
import { ItemCard } from './item-card';
import { ShareIconAction, ConfirmIconAction } from './quick-actions';
import {
  ListToolbar,
  SortOrder,
  ViewMode,
  sortItems,
  filterByTitle,
  useViewMode,
  useSelection,
  SelectionBar,
} from './list-controls';
import { mapWithConcurrency } from './concurrency';
import { SharedVideo } from './types';

export function SharedLibraryPage() {
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<SortOrder>('default');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useViewMode('shared-library');
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const selection = useSelection<SharedVideo>((v) => v.videoId);

  const query = useQuery({
    queryKey: ['shared-library'],
    queryFn: () => listSharedLibrary(),
  });

  const allRows = query.data ?? [];
  // Recomputed plainly each render — see the equivalent comment in library-page.tsx.
  const visibleRows = sortItems(filterByTitle(allRows, search, (r) => r.title), sort, (r) => r.title);

  async function handleBulkUnshare() {
    const ids = [...selection.selectedIds];
    if (ids.length === 0) return;
    if (!confirm(`Unshare ${ids.length} video${ids.length === 1 ? '' : 's'} from the shared library?`)) return;
    setBulkPending(true);
    setBulkSummary(null);
    const results = await mapWithConcurrency(ids, (id) => unshareVideo(id), 8);
    const succeeded = results.filter((r) => r.status === 'fulfilled').map((r) => r.item);
    const failed = results.filter((r) => r.status === 'rejected');
    if (succeeded.length > 0) {
      const succeededSet = new Set(succeeded);
      queryClient.setQueryData<SharedVideo[] | undefined>(['shared-library'], (prev) =>
        prev?.filter((r) => !succeededSet.has(r.videoId)),
      );
      succeeded.forEach((id) => selection.toggle(id));
    }
    setBulkSummary(
      failed.length === 0
        ? `Unshared ${succeeded.length} video${succeeded.length === 1 ? '' : 's'}.`
        : `Unshared ${succeeded.length} of ${ids.length}. ${failed.length} couldn't be unshared — try again.`,
    );
    setBulkPending(false);
  }

  if (query.isLoading) return <p className="muted">Loading shared library…</p>;

  if (allRows.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h2>Nothing's been shared yet</h2>
        <p className="muted">
          Videos published from the detail page of a ready upload will show up here for everyone.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1>Shared library</h1>
      <ListToolbar
        sort={sort}
        onSortChange={setSort}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onSearch={setSearch}
        searchPlaceholder="Search the shared library…"
        selectionMode={selectionMode}
        onToggleSelectionMode={() => {
          setSelectionMode((v) => !v);
          selection.clear();
          setBulkSummary(null);
        }}
      />
      {selectionMode && (
        <SelectionBar count={selection.count} onSelectAll={() => selection.selectAll(visibleRows)} onClear={selection.clear}>
          <button onClick={handleBulkUnshare} disabled={selection.count === 0 || bulkPending}>
            Unshare selected
          </button>
        </SelectionBar>
      )}
      {bulkSummary && <p className="muted">{bulkSummary}</p>}
      {visibleRows.length === 0 ? (
        <p className="muted">No videos match your search.</p>
      ) : viewMode === 'grid' ? (
        <div className="item-grid">
          {visibleRows.map((row) => (
            <SharedLibraryItem
              key={row.videoId}
              row={row}
              viewMode={viewMode}
              selectionMode={selectionMode}
              selected={selection.isSelected(row.videoId)}
              onToggleSelected={() => selection.toggle(row.videoId)}
            />
          ))}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {visibleRows.map((row) => (
            <SharedLibraryItem
              key={row.videoId}
              row={row}
              viewMode={viewMode}
              selectionMode={selectionMode}
              selected={selection.isSelected(row.videoId)}
              onToggleSelected={() => selection.toggle(row.videoId)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function SharedLibraryItem({
  row,
  viewMode,
  selectionMode,
  selected,
  onToggleSelected,
}: {
  row: SharedVideo;
  viewMode: ViewMode;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}) {
  const queryClient = useQueryClient();

  const unshareMutation = useMutation({
    mutationFn: () => unshareVideo(row.videoId),
    onSuccess: () => {
      queryClient.setQueryData<SharedVideo[] | undefined>(['shared-library'], (prev) =>
        prev?.filter((r) => r.videoId !== row.videoId),
      );
    },
  });

  const shareUrl = `https://${config.spaDomain}/watch/${row.videoId}`;

  return (
    <ItemCard
      href={`/shared/${row.videoId}`}
      thumbnailUrl={row.thumbnailUrl}
      title={row.title}
      viewMode={viewMode}
      selectionMode={selectionMode}
      checkbox={{ checked: selected, onChange: onToggleSelected }}
      onRename={async (next) => {
        await editSharedVideo(row.videoId, { title: next });
        queryClient.setQueryData<SharedVideo[] | undefined>(['shared-library'], (prev) =>
          prev?.map((r) => (r.videoId === row.videoId ? { ...r, title: next } : r)),
        );
      }}
      meta={<span className="muted">Added {formatRelativeTime(row.addedAt)}</span>}
      actions={
        <>
          <ShareIconAction shareUrl={shareUrl} />
          <ConfirmIconAction
            icon="🚫"
            label="Unshare"
            confirmMessage="Unshare this video from the shared library?"
            onConfirm={() => unshareMutation.mutate()}
            disabled={unshareMutation.isPending}
          />
        </>
      }
    />
  );
}
